import React, { useEffect, useState, useCallback } from "react";
import { ChartHeader } from "assets/css/global";
import { useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import AlertModal from "components/AlertModal";
import * as moment from "moment";
import 'chart.js/auto';
import { Chart as ChartJS, CategoryScale, PointElement, LineElement, LinearScale, BarElement, Title, Tooltip, Legend, LineController } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { qtySpendChartApi } from "redux/actions/dashboard";
import { formatCurrencyByCountry, getLoggedInUserCountry } from "helpers";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, LineController);

const LineChart = (props) => {
  const { chartFilters } = props
  const loggedInUserInfo = JSON.parse(localStorage.getItem('loggedInUserData'))
  const userCountry = getLoggedInUserCountry();
  const getSpendDecimalLimit = (value) => Math.abs(Number(value)) >= 99 ? 0 : 2;
  const token = loggedInUserInfo?.token
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const [xAxisValues, setXAxisValues] = useState()
  const [qtySpendGraphApiData, setQtyrSpendGraphApiData] = useState()
  const [barChartDateset, setBarChartDateset] = useState([])
  const [lineChartDateset, setLineChartDateset] = useState([])

  const [modal, setModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [modalLink, setModalLink] = useState("/");
  const toggleModal = () => setModal(!modal);

  useEffect(() => {
    if (chartFilters) fetchQtySpendData()
  }, [chartFilters])

  const fetchQtySpendData = async () => {
    let data = {
      locationIds: chartFilters?.locationIdList,
      startDate: moment(chartFilters?.fromDate).format('YYYY-MM-DD'),
      endDate: moment(chartFilters?.toDate).format('YYYY-MM-DD'),
      country: userCountry
    }
    let response = await dispatch(qtySpendChartApi(data, token))
    if (response.data) {
      setQtyrSpendGraphApiData(response.data)
      setQtySpendChartPoints(activeTab, response.data)
    } else {
      if (response?.data?.error?.message === "Invalid Token") {
        navigate("/")
      } else {
        setModalContent(response?.data?.error?.message || response.message || props.locale('genericErrorLater'));
        setModalLink("");
        toggleModal();
      }
    }
  }

  const setQtySpendChartPoints = (activeTab, data) => {
    setXAxisValues([])
    setLineChartDateset([])
    setBarChartDateset([])
    if (activeTab === 0) {
      if (data?.monthly?.length && data?.monthly?.length > 0) {
        let tempXAxisValues = data?.monthly?.map(data => data?.date)
        let tempQtyPoints = data?.monthly?.map(data => data?.totalQuantity)
        let tempSpendPoints = data?.monthly?.map(data => data?.totalAmount)
        setXAxisValues(tempXAxisValues)
        setLineChartDateset(tempQtyPoints)
        setBarChartDateset(tempSpendPoints)
      }
    } else {
      if (data?.weekly?.length && data?.weekly?.length > 0) {
        let tempXAxisValues = data?.weekly?.map(data => data?.date)
        let tempQtyPoints = data?.weekly?.map(data => data?.totalQuantity)
        let tempSpendPoints = data?.weekly?.map(data => data?.totalAmount)
        setXAxisValues(tempXAxisValues)
        setLineChartDateset(tempQtyPoints)
        setBarChartDateset(tempSpendPoints)
      }
    }
  }

  const data = {
    labels: xAxisValues,
    datasets: [
      {
        label: props.locale('quantity'),
        type: 'line',
        data: lineChartDateset,
        borderColor: '#ed7d31',
        borderWidth: 3,
        fill: false,
        yAxisID: 'yAxisQuantity',
      },
      {
        label: props.locale('spend'),
        type: 'bar',
        data: barChartDateset,
        backgroundColor: '#5b9bd5',
        borderColor: '#5b9bd5',
        borderWidth: 3,
        yAxisID: 'yAxisSpend',
        // barPercentage: 0.7,
        barThickness: Math.max(10, 60 - barChartDateset.length * 3),
      },
    ],
  };

  const options = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          fontSize: 14,
          boxWidth: 12,
          usePointStyle: false,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            const value = context.parsed.y;
            const formatted = context.datasetIndex === 1
              ? formatCurrencyByCountry(value, userCountry, getSpendDecimalLimit(value)) : value.toLocaleString('en-US');
            return label + formatted;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'category',
        labels: xAxisValues,
        grid: {
          display: false,
        },
      },
      yAxisSpend: {
        position: 'left',
        grid: {
          display: false,
        },
        ticks: {
          callback: function (value) {
            return formatCurrencyByCountry(value, userCountry, getSpendDecimalLimit(value));
          },
        },
      },
      yAxisQuantity: {
        position: 'right',
        grid: {
          display: true,
        },
      },
    },
  };

  return (
    <div className="lineBarChartWrapper">
      <div>
        <ChartHeader>
          <h4 style={{ fontSize: '13px' }}>{props.locale('quantitySpendTrend')}</h4>
          <div className="header-btn-group">
            <button className={`tablink ${activeTab === 0 ? "active" : ""}`} onClick={() => {
              setQtySpendChartPoints(0, qtySpendGraphApiData)
              setActiveTab(0)
            }}>{props.locale('monthly')}</button>
            <button className={`tablink ${activeTab === 1 ? "active" : ""}`} onClick={() => {
              setQtySpendChartPoints(1, qtySpendGraphApiData)
              setActiveTab(1)
            }}>{props.locale('weekly')}</button>
          </div>
        </ChartHeader>
        <div className="chart-wrapper" style={{ marginTop: '30px' }}>
          <Bar options={options} data={data} />
        </div>
      </div>
      <div style={{ textAlign: 'end' }}>
        <h4 style={{ fontSize: '14px', fontFamily: 'latobold', fontStyle: 'italic', color: '#005586', cursor: 'pointer' }}>{props.locale('excludesTaxes')}</h4>
      </div>

      <AlertModal
        isOpen={modal}
        toggle={toggleModal}
        title={props.locale('alert')}
        content={modalContent}
        link={modalLink}
        className="alert-msg-container"
        InnerClassName="alert-msg"
      />

    </div>
  )
};

export default LineChart;
